import { Linking } from 'react-native'
import { useDispatch } from 'APP/Hooks/redux'
import { NavigationProp, useNavigation } from '@react-navigation/native'
import Config from 'APP/Config'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import { handleNavigationContactInfo } from 'APP/Helpers/handleNavigationContactInfo'
import sprigClient from 'APP/Services/Sprig'
import Analytics from 'APP/Services/Analytics'
import { handleMicroAppNavigation } from 'APP/Lib/MicroAppNavHelper'

export const serviceGroupActionType = {
  episodeCreation: 'episode_creation',
  url: 'url',
  navigation: 'navigation',
  showContactInfo: 'show_contact_info',
} as const

export type ServiceGroupActionType =
  (typeof serviceGroupActionType)[keyof typeof serviceGroupActionType]

export interface ContactInfo {
  id?: string | number
  name?: string
  actionType?: string
  screenName?: string
  navigationUrl?: string
}

export interface RawBenefitItem {
  id?: string | number
  name?: string
  title?: string
  url?: string
  actionType?: ServiceGroupActionType
  actionValue?: string
  contentType?: string
  navigationUrl?: string
  contactInfo?: ContactInfo | null
  placeholderHealthIssueTypeId?: string | number | null
}

export type BenefitItem = RawBenefitItem

export type HandleBenefitsPress = (
  item: BenefitItem | null,
  analyticsScreenName?: string,
  serviceGroup?: Record<string, unknown> | null
) => void

const useHandleOnBenefitsPress = (): HandleBenefitsPress => {
  const navigation = useNavigation<NavigationProp<Record<string, object>>>()
  const dispatch = useDispatch()

  const episodeDispatch = (
    actionValue?: string,
    placeholderHealthIssueTypeId?: string | number | null
  ): void => {
    if (actionValue === Config.EPISODE_CREATION_COMMANDS?.wbiAssessment) {
      dispatch(PatientHistoryActions.getOrCreateWbiEpisodeRequest())
    } else {
      // TODO: pass flowType when available
      dispatch(
        PatientHistoryActions.launchEpisodeCommand(
          actionValue,
          placeholderHealthIssueTypeId,
          undefined
        )
      )
    }
  }

  const navigateToMicroApp = (): void => {
    const appId = 'cliniaHealthNavigation'
    handleMicroAppNavigation(true, { microAppProps: { appId } })
  }

  return (item, analyticsScreenName, serviceGroup): void => {
    const { name, url, id, title, placeholderHealthIssueTypeId } = item ?? {}
    const actionType = item?.actionType
    const actionValue = item?.actionValue
    const contentType = item?.contentType
    const navigationUrl = item?.navigationUrl
    const contactInfo = item?.contactInfo ?? null

    if (name) {
      sprigClient.track(`TILE_CLICKED_${name}`)
    }

    Analytics.trackEvent('button_click', {
      button_value: `serviceAction : ${contentType} : ${id} : ${name}`,
      button_text: title,
      trigger: analyticsScreenName,
    })

    if (actionType === serviceGroupActionType.episodeCreation) {
      episodeDispatch(actionValue, placeholderHealthIssueTypeId)
    } else if (actionType === serviceGroupActionType.url || url) {
      const targetUrl = actionValue ?? url

      const canOpenUrl = !!targetUrl && Linking.canOpenURL(targetUrl)

      if (canOpenUrl) {
        Linking.openURL(targetUrl)
      }
    } else if (actionType === serviceGroupActionType.navigation && !navigationUrl) {
      navigateToMicroApp()
    } else if (actionType === serviceGroupActionType.navigation && navigationUrl) {
      if (actionValue) {
        navigation.navigate(actionValue, {
          navigationUrl,
          serviceGroup: serviceGroup ?? null,
        })
      }
    } else if (actionType === serviceGroupActionType.showContactInfo && contactInfo) {
      handleNavigationContactInfo(contactInfo, navigation)
    }
  }
}

export default useHandleOnBenefitsPress
